import { Link } from '@tanstack/react-router';
import { ArrowRight, SprayCan } from 'lucide-react';
import type { OperationsApartmentTurnover, OperationsTravelGroup } from '@/shared/api';
import { BottomSheet } from '@/shared/components';
import { cn } from '@/shared/lib/cn';
import {
  cleaningTaskPresentation,
  cleaningTaskToneClass,
  orderedCleaningTasks,
} from './operationsPresentation';
import { formatCalendarDate } from './operationsDate';
import { cleaningWindow, detailSummary as summary } from './heuteDetailModel';
import { HeuteTravelGroupNote } from './HeuteTravelGroupNote';
import { OperationsStayFacts } from './OperationsStayFacts';

interface Props {
  apartment: OperationsApartmentTurnover | null;
  checkInTime: string;
  checkOutTime: string;
  /** Der gezeigte Tag; ohne eigene Aufgabe führt der Weg zur Reinigung dieses Tages. */
  date: string;
  /** Die Reisegruppen des Tages mit diesem Apartment – am Wechseltag abreisende und anreisende. */
  travelGroups?: OperationsTravelGroup[];
  onClose: () => void;
}

/**
 * Tagesdetails eines Apartments (Board „Heute · Details“): am Desktop als Panel rechts, am Handy
 * als Blatt. Abreise → Reinigung → Anreise mit Kontakten, darunter die Schnellaktionen.
 */
export function HeuteDetailPanel({
  apartment,
  checkInTime,
  checkOutTime,
  date,
  travelGroups = [],
  onClose,
}: Props) {
  const tasks = apartment ? orderedCleaningTasks(apartment) : [];
  const departure = apartment?.stays.find((stay) => stay.stayPhase === 'Departure');
  const arrival = apartment?.stays.find((stay) => stay.stayPhase === 'Arrival');
  const others = apartment?.stays.filter((stay) => stay !== departure && stay !== arrival) ?? [];
  const facts = (stay: NonNullable<typeof departure>) => (
    <OperationsStayFacts
      key={stay.bookingId}
      stay={stay}
      checkInTime={checkInTime}
      checkOutTime={checkOutTime}
    />
  );
  return (
    <BottomSheet
      open={apartment !== null}
      onOpenChange={(open) => !open && onClose()}
      title={apartment?.adminDisplayName ?? ''}
      description={apartment ? summary(apartment, checkInTime, checkOutTime) : undefined}
      desktop="panel"
    >
      {apartment && (
        <div className="flex flex-col gap-5 pb-2">
          {travelGroups.map((group) => (
            <HeuteTravelGroupNote key={group.id} group={group} propertyId={apartment.propertyId} />
          ))}
          <div className="flex flex-col gap-4">
            {departure && facts(departure)}
            <section
              aria-label="Reinigung"
              className="bg-paper rounded-control flex flex-col gap-1 px-3 py-2.5"
            >
              <p className="text-ink flex items-center gap-2 text-sm font-bold">
                <SprayCan size={16} aria-hidden /> Reinigung
              </p>
              {tasks.length ? (
                tasks.map((task) => (
                  <p key={task.id} className="text-xs">
                    <span className={cn('font-semibold', cleaningTaskToneClass(task))}>
                      {cleaningTaskPresentation(task).label}
                    </span>
                    <span className="text-ink-soft">
                      {' '}
                      · {cleaningWindow(task, date)}
                      {task.isUnassigned ? ' · nicht zugewiesen' : ''}
                    </span>
                  </p>
                ))
              ) : (
                <p className="text-ink-soft text-xs">Keine Reinigungsaufgabe an diesem Tag.</p>
              )}
            </section>
            {arrival && facts(arrival)}
            {others.map(facts)}
          </div>
          <div className="flex flex-col gap-2">
            <p className="text-ink-soft font-mono text-[11px] font-semibold uppercase tracking-[0.14em]">
              Schnellaktionen
            </p>
            {apartment.stays.map((stay) => (
              <Link
                key={stay.bookingId}
                to="/bookings/$id"
                params={{ id: stay.bookingId }}
                className="bg-forest rounded-pill flex min-h-12 items-center justify-between px-5 text-sm font-bold text-white no-underline"
              >
                Buchung öffnen
                {apartment.stays.length > 1
                  ? ` · ${stay.guestName ?? stay.reference ?? `ab ${formatCalendarDate(stay.checkInDate)}`}`
                  : ''}
                <ArrowRight size={16} aria-hidden />
              </Link>
            ))}
            {tasks[0] ? (
              <Link
                to="/cleaning/$id"
                params={{ id: tasks[0].id }}
                className="bg-card-alt text-ink rounded-pill flex min-h-12 items-center justify-between px-5 text-sm font-bold no-underline"
              >
                Reinigung öffnen
                <ArrowRight size={16} aria-hidden />
              </Link>
            ) : (
              <Link
                to="/cleaning"
                search={{ view: undefined, date, status: undefined, assignee: undefined }}
                className="bg-card-alt text-ink rounded-pill flex min-h-12 items-center justify-between px-5 text-sm font-bold no-underline"
              >
                Zur Reinigung
                <ArrowRight size={16} aria-hidden />
              </Link>
            )}
          </div>
        </div>
      )}
    </BottomSheet>
  );
}
